/**
 * The Plan Audit for one plan version (PRD: docs/9-exploration/PRD-plan-audit.md).
 *
 * Read-only. Loads the version's items through planAuditService, runs the
 * existing relationship detection (computeViolations) over them with the same
 * relationship store every other caller reads, and words the result with
 * planAuditCore. Nothing is written and nothing is stored.
 */
import { useEffect, useMemo, useState } from 'react';
import { computeViolations } from '../lib/poolViolations';
import { loadPlanAuditInputs, type PlanAuditInputs } from '../lib/planAuditService';
import { runPlanAudit, type PlanAuditResult } from '../lib/planAuditCore';
import { useItemRelationshipsStore } from '../stores/prioritization/itemRelationshipsStore';

export interface PlanAuditState {
  loading: boolean;
  error: string | null;
  result: PlanAuditResult | null;
}

export function usePlanAudit(planVersionId: string | null, workspaceId: string | null): PlanAuditState {
  const edges = useItemRelationshipsStore((s) => s.edges);
  const groups = useItemRelationshipsStore((s) => s.groups);
  const memberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const lineageEvents = useItemRelationshipsStore((s) => s.lineageEvents);
  const relationshipsSynced = useItemRelationshipsStore((s) => s.syncState.lastSyncAt != null);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const [inputs, setInputs] = useState<PlanAuditInputs | null>(null);
  // True once a relationship read has been attempted, so a failed or
  // unavailable read cannot leave the panel loading forever.
  const [relationshipsTried, setRelationshipsTried] = useState(relationshipsSynced);

  useEffect(() => {
    if (relationshipsSynced) {
      setRelationshipsTried(true);
      return;
    }
    let cancelled = false;
    void fetchRelationships().finally(() => {
      if (!cancelled) setRelationshipsTried(true);
    });
    return () => {
      cancelled = true;
    };
  }, [relationshipsSynced, fetchRelationships]);

  useEffect(() => {
    if (!planVersionId) {
      setInputs(null);
      return;
    }
    let cancelled = false;
    setInputs(null);
    void loadPlanAuditInputs(planVersionId, workspaceId).then((next) => {
      if (!cancelled) setInputs(next);
    });
    return () => {
      cancelled = true;
    };
  }, [planVersionId, workspaceId]);

  const result = useMemo(() => {
    if (!inputs || inputs.error) return null;
    const memberIds = inputs.items.map((i) => i.id);
    const violations =
      memberIds.length >= 2
        ? computeViolations(memberIds, {
            items: inputs.items.map((i) => ({ id: i.id, title: i.title, itemCategory: i.itemCategory, altitudeLevel: i.altitudeLevel })),
            edges: edges ?? [],
            groups: groups ?? [],
            memberships: memberships ?? [],
            lineageEvents: lineageEvents ?? [],
          }).violations
        : [];
    return runPlanAudit({
      items: inputs.items,
      uncheckableCount: inputs.uncheckableCount,
      violations,
      unlinkedItemIds: inputs.unlinkedItemIds,
      cycle: inputs.cycle,
      cycleUnreadReason: inputs.cycleUnreadReason,
    });
  }, [inputs, edges, groups, memberships, lineageEvents]);

  return {
    loading: planVersionId != null && (inputs === null || !relationshipsTried),
    error: inputs?.error ?? null,
    result,
  };
}
